import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useT } from '../../i18n';
import { getStore } from '../../persistence/store';
import { uebertrageAlles } from '../../platform/anstoss';
import { appendAppLog } from '../../platform/appLog';
import { geraeteName } from '../../platform/appVersion';
import { Signing } from '../../platform/signing';
import { ServerFehler } from '../../server/client';
import { createSync } from '../../server/sync';
import { baueClassicSender } from '../../server/classic/takt';
import {
  baueClient,
  istEingerichtet,
  ladeZugang,
  speichereZugang,
  verwalteteEinstellungen,
  VORGABE,
  type Servermodus,
} from '../../server/zugang';
import { useAppStore } from '../../store/useAppStore';
import { meldeRueckstand } from '../header/rueckstandMelden';
import { inOberflaeche } from './einstellungsFelder';
import styles from './Sheets.module.css';

/**
 * Server und Demo in den Einstellungen.
 *
 * Zwei Dinge stehen hier absichtlich nebeneinander: **wohin übertragen wird** und **woher eine
 * Prüfung kommt, wenn es keinen Server gibt**. Der zweite Fall ist derzeit der Normalfall —
 * Relay kann den Inhalt einer Prüfung noch nicht liefern (`docs/spec-phase4-server.md` §7).
 *
 * Das Passwort wird **nicht** angezeigt, auch nicht nachträglich: das Feld bleibt leer, und
 * leer heisst „unverändert lassen". Wer es löschen will, trägt ein Leerzeichen ein und sichert.
 */
export function ServerGroup({ Group }: { Group: (p: { label: string; children: ReactNode }) => ReactNode }) {
  const t = useT();
  const clientId = useAppStore((s) => s.clientId);
  const deviceName = useAppStore((s) => s.deviceName);

  const [url, setUrl] = useState('');
  const [modus, setModus] = useState<Servermodus>('relay');
  const [login, setLogin] = useState('');
  const [passwort, setPasswort] = useState('');
  const [hatPasswort, setHatPasswort] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Der Schlüsselbund hat das Passwort abgelehnt — es gilt nur für diese Sitzung. */
  const [fluechtig, setFluechtig] = useState(false);
  /**
   * Auf dem iPad kommen die Zugangsdaten aus der iOS-Einstellungen-App. Dann sind die Felder
   * hier nur eine **Anzeige** — sie gehören der Einrichtung, nicht dem Prüfer, und der
   * geführte Zugriff hält ihn während der Prüfung ohnehin von den Einstellungen fern.
   */
  const [verwaltet, setVerwaltet] = useState(false);

  // Die Zahlen stehen seit 2026-10-02 aufgeschlüsselt unten im Dialog (`RueckstandGruppe`);
  // hier wird nach einem Durchgang nur neu gezählt.
  const offeneZaehlen = meldeRueckstand;

  useEffect(() => {
    let aktuell = true;
    void (async () => {
      if (!clientId) return;
      const vonIOS = await verwalteteEinstellungen();
      const zugang = await ladeZugang(getStore(), clientId);
      if (!aktuell) return;
      setVerwaltet(vonIOS.available);
      setModus(zugang.modus);
      // Vorbelegung nur in die Felder, nicht in die Ablage: eingerichtet ist der Server erst,
      // wenn der Prüfer einmal gesichert hat (s. `VORGABE` in `server/zugang.ts`).
      setUrl(zugang.url || VORGABE.url);
      setLogin(zugang.login || VORGABE.login);
      setPasswort(vonIOS.available || zugang.password !== '' ? '' : VORGABE.password);
      setHatPasswort(zugang.password !== '');
    })();
    void offeneZaehlen();
    return () => {
      aktuell = false;
    };
  }, [clientId, offeneZaehlen]);

  /**
   * Aus einem Fehler eine Zeile machen. `ServerFehler` sagt bereits vollständig, was war —
   * ein Vorsatz davor ergäbe „Keine Verbindung: Keine Verbindung zum Server".
   */
  const alsZeile = useCallback(
    (fehler: unknown) =>
      fehler instanceof ServerFehler
        ? fehler.message
        : t.server.failed(fehler instanceof Error ? fehler.message : String(fehler)),
    [t],
  );

  /** Sichern und gleich ausprobieren — eine Adresse ohne Probe sagt nichts. */
  const verbinden = useCallback(async () => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const store = getStore();
      const alt = await ladeZugang(store, clientId);
      const zugang = {
        url: url.trim(),
        login: login.trim(),
        // Leeres Feld heisst „unverändert" — sonst löschte jedes Sichern das Passwort.
        password: passwort === '' ? alt.password : passwort,
        modus,
      };
      const { passwortDauerhaft } = verwaltet
        ? { passwortDauerhaft: true }
        : await speichereZugang(store, clientId, zugang);
      setHatPasswort(zugang.password !== '');
      setPasswort('');
      if (!passwortDauerhaft) {
        // Nicht verschweigen: sonst steht beim nächsten Start ein leeres Feld da, und niemand
        // weiss, warum die Übertragung plötzlich nicht mehr geht.
        await appendAppLog('Server: Passwort nur für diese Sitzung — Schlüsselbund lehnt ab.', 'warn');
      }
      setFluechtig(!passwortDauerhaft);

      if (!istEingerichtet(zugang)) {
        setMeldung({ art: 'schlecht', text: t.server.none });
        return;
      }

      const identity = await Signing.getIdentity().catch(() => null);
      const client = baueClient(zugang, {
        deviceIdentifier: identity?.deviceId ?? '',
        deviceName: geraeteName() || (identity?.deviceName ?? ''),
      });
      const anmeldung = await client.anmelden();
      // ⚠️ Eine leere `serverTime` heisst „niemand hat gemessen", nicht „Versatz null". Der
      // Classic-Server liefert weder Zeit noch Versatz; ein „Versatz 0 s" dort wäre eine
      // Behauptung über eine Messung, die es nie gab.
      const gemessen = anmeldung.serverTime !== '';
      const weitAuseinander = gemessen && Math.abs(anmeldung.driftSeconds) >= 30;
      // ⬅️ **Befund des Nutzers vom 2026-09-17:** hier stand `connected(serverTime || '—')`, und
      // bei einem Server ohne Zeitangabe kam „Verbunden. Serverzeit —." heraus. Das sagt nichts:
      // der Gedankenstrich ist keine Auskunft, sondern eine Lücke mit Satzzeichen. Jetzt sagt
      // die Meldung im ungemessenen Fall, **dass** und **warum** keine Zeit dasteht.
      const text = gemessen
        ? t.server.connected(anmeldung.serverTime) +
          (weitAuseinander ? ' ' + t.server.drift(Math.round(anmeldung.driftSeconds)) : '')
        : t.server.connectedOhneZeit;
      setMeldung({ art: weitAuseinander ? 'schlecht' : 'gut', text });
      await appendAppLog(
        'Server: angemeldet (' + modus + ')' +
          (gemessen ? ', Versatz ' + String(anmeldung.driftSeconds) + ' s, Serverzeit ' + anmeldung.serverTime : ''),
      );
    } catch (fehler) {
      setMeldung({ art: 'schlecht', text: alsZeile(fehler) });
      await appendAppLog('Server: Verbindung fehlgeschlagen — ' + String(fehler), 'warn');
    } finally {
      setLaeuft(false);
    }
  }, [alsZeile, clientId, deviceName, login, modus, passwort, t, url]);

  const jetztUebertragen = useCallback(async () => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const store = getStore();
      const zugang = await ladeZugang(store, clientId);
      if (!istEingerichtet(zugang)) {
        setMeldung({ art: 'schlecht', text: t.server.none });
        return;
      }
      const identity = await Signing.getIdentity().catch(() => null);
      const client = baueClient(zugang, {
        deviceIdentifier: identity?.deviceId ?? '',
        deviceName: geraeteName() || (identity?.deviceName ?? ''),
      });

      if (zugang.modus === 'classic') {
        /*
         * ⚠️ **Nicht `createSync`.** Der spricht Relay-Pfade (`/rest/v1/…`), die am
         * Classic-Server in einen 404 liefen. Und die Übersetzung ist keine Formsache: Classic
         * will je Bewertung den **vollständigen Stand**, nicht das einzelne Ereignis —
         * siehe `server/classic/bewertungen.ts`.
         */
        const sender = baueClassicSender(store, zugang, identity, clientId);
        const ergebnis = await sender.jetztSenden();
        // Die Nachricht des Prüfungsleiters hat Vorrang: der Server liefert sie genau einmal
        // aus und löscht sie dann. Wer sie hier verschluckt, hat sie verloren.
        if (ergebnis.nachricht) {
          setMeldung({ art: 'gut', text: t.server.messageFromServer(ergebnis.nachricht) });
        } else if (ergebnis.liegengeblieben > 0) {
          setMeldung({ art: 'schlecht', text: t.server.sendStuck(ergebnis.liegengeblieben) });
        } else {
          setMeldung({ art: 'gut', text: t.server.sent(ergebnis.uebertragen) });
        }
        return;
      }

      const sync = createSync({ store, client, log: appendAppLog });
      const ergebnis = await sync.jetztUebertragen();
      setMeldung({ art: 'gut', text: t.server.sent(ergebnis.uebertragen) });
    } catch (fehler) {
      setMeldung({ art: 'schlecht', text: alsZeile(fehler) });
    } finally {
      /*
       * ⚠️ **Auch die Dateien.** Oben gehen nur die Bewertungen hinaus; Fotos, Videos und Abzüge
       * schickt der Takt. Ruht `auto_upload`, ist dieser Knopf der einzige Weg für sie — also
       * hier einen Durchgang von Hand anstossen. Fehler meldet der Takt selbst ins Dateilog.
       */
      await uebertrageAlles().catch(() => undefined);
      setLaeuft(false);
      void offeneZaehlen();
    }
  }, [alsZeile, clientId, deviceName, offeneZaehlen, t]);

  /*
   * ⛔ **Die Demo-Prüfung wird hier nicht mehr eingelesen** (Auftrag des Nutzers, 2026-09-19).
   * Der Knopf stand bis dahin als letzte Gruppe unter dem Serverzugang. Sie kommt jetzt über
   * den Schalter `demo_install` in den iPad-Einstellungen und wird beim nächsten Wechsel in den
   * Vordergrund eingelesen (`server/demoSchalter.ts`).
   *
   * **Warum weg:** der Import ersetzt eine vorhandene Beispielprüfung samt der Bewertungen
   * daran. Ein Knopf im App-Dialog liess sich mitten in einer Prüfung drücken; die
   * iPad-Einstellungen sind im geführten Zugriff nicht erreichbar.
   */

  /**
   * Die vier Zugangsfelder: Protokoll, Adresse, Anmeldename, Passwort.
   *
   * `!verwaltet` ist der Browser-Rückfall — dort gibt es keine iPad-Einstellungen, und ohne die
   * Felder liesse sich am Rechner nichts einrichten.
   */
  const zeigeZugangsfelder = inOberflaeche('server_url') || !verwaltet;

  return (
    <>
      {Group({
        label: t.server.group,
        children: (
          <>
            {/*
              ⚠️ **Auf dem Gerät gibt es diese Felder hier nicht** (Entscheidung des Nutzers,
              2026-09-13): Protokoll, Adresse, Anmeldename und Passwort gehören der
              Einrichtung und stehen ausschliesslich in den iPad-Einstellungen — nur von dort
              kann ein MDM sie setzen. Sie hier auch nur anzuzeigen lud dazu ein, sie für
              änderbar zu halten.

              Im **Browser** bleiben sie stehen: dort gibt es keine iPad-Einstellungen, und
              ohne die Felder wäre die Entwicklung am Rechner nicht mehr zu bedienen. Der
              Browser ist dabei Werkzeug, nicht Ziel.

              ⚠️ **Seit dem 2026-09-17 entscheidet der Katalog mit.** Steht `server_url` dort
              auf „in der Oberfläche", erscheinen die Felder auch auf dem Gerät — dann hat der
              Nutzer es ausdrücklich so gesetzt. Steht es auf „nicht", bleibt es beim alten
              Verhalten. Die beiden Aussagen sagen damit dasselbe, statt sich zu widersprechen.
            */}
            {zeigeZugangsfelder && (
              <>
            <label className={styles.textRow}>
              <span className={styles.textLabel}>{t.server.mode}</span>
              <select
                className={styles.textInput}
                value={modus}
                disabled={verwaltet}
                onChange={(e) => setModus(e.target.value === 'classic' ? 'classic' : 'relay')}
              >
                <option value="relay">Relay</option>
                <option value="classic">Classic</option>
              </select>
            </label>
            <label className={styles.textRow}>
              <span className={styles.textLabel}>{t.server.url}</span>
              <input
                className={styles.textInput}
                value={url}
                inputMode="url"
                autoCapitalize="off"
                autoCorrect="off"
                placeholder={t.server.urlPlaceholder}
                readOnly={verwaltet}
                disabled={verwaltet}
                onChange={(e) => setUrl(e.target.value)}
              />
            </label>
            <label className={styles.textRow}>
              <span className={styles.textLabel}>{t.server.login}</span>
              <input
                className={styles.textInput}
                value={login}
                autoCapitalize="off"
                autoCorrect="off"
                readOnly={verwaltet}
                disabled={verwaltet}
                onChange={(e) => setLogin(e.target.value)}
              />
            </label>
            <label className={styles.textRow}>
              <span className={styles.textLabel}>{t.server.password}</span>
              <input
                className={styles.textInput}
                type="password"
                value={passwort}
                placeholder={verwaltet || hatPasswort ? '••••••••' : ''}
                readOnly={verwaltet}
                disabled={verwaltet}
                autoCapitalize="off"
                autoCorrect="off"
                onChange={(e) => setPasswort(e.target.value)}
              />
            </label>
              </>
            )}

            <div className={styles.serverActions}>
              <button type="button" className={`tap ${styles.stufe}`} disabled={laeuft} onClick={() => void verbinden()}>
                {laeuft ? t.server.testing : verwaltet ? t.server.connect : t.server.save}
              </button>
              <button
                type="button"
                className={`tap ${styles.stufe}`}
                disabled={laeuft}
                onClick={() => void jetztUebertragen()}
              >
                {t.server.sendNow}
              </button>
            </div>

            {fluechtig && (
              <div className={styles.textNote} style={{ color: 'var(--warn, #b4540a)' }}>
                {t.server.passwordVolatile}
              </div>
            )}
            {meldung && (
              <div className={styles.textNote} data-art={meldung.art} style={{ color: meldung.art === 'gut' ? 'var(--ok, #0a7d4b)' : 'var(--warn, #b4540a)' }}>
                {meldung.text}
              </div>
            )}
          </>
        ),
      })}
    </>
  );
}
